import {
  Badge,
  Box,
  Container,
  Flex,
  Text,
  Select,
  Switch,
  TextInput,
  Button,
  Group,
  Breadcrumbs,
  Anchor,
  NumberInput,
  Modal,
  ActionIcon,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconSearch, IconDownload, IconCalendar, IconBuilding, IconEdit, IconPrinter } from "@tabler/icons";
import { MantineReactTable } from "mantine-react-table";
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useQuery } from "react-query";
import AppBreadcrumb from "components/AppBreadcrumb";
import moment from "moment";
import { httpClient } from "lib/axios";
import { getCounterId, isCounterAdmin } from "pages/Auth/permissionGuard";
import { counters_api } from "pages/master/counter/counter.service";
import { getAlteredCounters } from "pages/master/customers/helperFunctions";
import { CSVLink } from "react-csv";
import { showErrorToast, showSuccessToast } from "utilities/Toast";
import { useReactToPrint } from "react-to-print";

export default function StockReport() {
  const isMobileScreen = useMediaQuery("(max-width: 768px)");

  // ─── Modes ──────────────────────────────────────────────────────────────────
  // 'counter_based' or 'expiry_based'
  const [activeTab, setActiveTab] = useState("counter_based");

  // ─── Filter States ──────────────────────────────────────────────────────────
  const [countersList, setCountersList] = useState([]);
  const [counter, setCounter] = useState(isCounterAdmin() ? getCounterId() : "");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [negativeStock, setNegativeStock] = useState(false);
  const [expiredStock, setExpiredStock] = useState(false);
  const [days, setDays] = useState(null);

  // Predefined expiry day options
  const expiryDaysOptions = [
    { value: "5", label: "5 Days" },
    { value: "15", label: "15 Days" },
    { value: "30", label: "30 Days" },
    { value: "50", label: "50 Days" },
    { value: "100", label: "100 Days" },
    { value: "custom", label: "Custom Days..." },
  ];
  const [selectedDaysOption, setSelectedDaysOption] = useState(null);
  const [customDays, setCustomDays] = useState(30);

  // ─── CSV Export States ──────────────────────────────────────────────────────
  const [csvData, setCsvData] = useState([]);
  const [isCsvDownloading, setIsCsvDownloading] = useState(false);
  const csvRef = useRef();

  // ─── Edit Modal States ──────────────────────────────────────────────────────
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [newStock, setNewStock] = useState(0);
  const [newExpDate, setNewExpDate] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // ─── Print Ref ──────────────────────────────────────────────────────────────
  const printRef = useRef();
  const [printData, setPrintData] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);

  // ─── Pagination ─────────────────────────────────────────────────────────────
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // ─── Fetch Counters ─────────────────────────────────────────────────────────
  useQuery({
    queryKey: ["counters-data-list-report"],
    queryFn: () => counters_api.get(),
    onSuccess: (data) => {
      setCountersList(
        getAlteredCounters(data.data).filter((c) => c.label.toLowerCase() !== "warehouse")
      );
    },
  });

  // Reset pagination when filter/tabs change
  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [activeTab, counter, search, negativeStock, expiredStock, days]);

  // Update actual days parameter based on chosen option
  useEffect(() => {
    if (selectedDaysOption === "custom") {
      setDays(customDays);
    } else if (selectedDaysOption) {
      setDays(parseInt(selectedDaysOption));
    } else {
      setDays(null);
    }
  }, [selectedDaysOption, customDays]);

  // ─── Fetch Report Data ──────────────────────────────────────────────────────
  const { data: reportData, isLoading, isRefetching, refetch: refetchReport } = useQuery({
    queryKey: [
      "stock-report-data",
      activeTab,
      counter,
      search,
      negativeStock,
      expiredStock,
      days,
      pagination.pageIndex,
      pagination.pageSize,
    ],
    queryFn: async () => {
      const daysParam = expiredStock ? "" : days !== null ? days : "";
      const res = await httpClient.get(
        `stock_report/?type=${activeTab}&counter_id=${counter || ""}&negative_stock=${negativeStock}&expired_stock=${expiredStock}&days=${daysParam}&search=${search}&page_number=${
          pagination.pageIndex + 1
        }&page_size=${pagination.pageSize}`
      );
      return res.data;
    },
    keepPreviousData: true,
  });

  // ─── Save Edit Handler ──────────────────────────────────────────────────────
  const handleSaveEdit = async () => {
    if (!editingRow) return;
    setIsSaving(true);
    try {
      const res = await httpClient.put("stock_report/", {
        id: editingRow.id,
        counter_id: counter || "",
        stock: newStock,
        exp_date: newExpDate,
      });

      if (res.data && res.data.success) {
        showSuccessToast({ title: "Success", message: "Stock and Expiry updated successfully" });
        setEditModalOpened(false);
        setEditingRow(null);
        refetchReport();
      } else {
        showErrorToast({ title: "Update Error", message: res.data.message || "Failed to update." });
      }
    } catch (err) {
      showErrorToast({
        title: "Update Error",
        message: err.response?.data?.message || err.message || "An error occurred.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Print Handler ──────────────────────────────────────────────────────────
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  const handlePrintAll = async () => {
    setIsPrinting(true);
    try {
      const daysParam = expiredStock ? "" : days !== null ? days : "";
      const res = await httpClient.get(
        `stock_report/?type=${activeTab}&counter_id=${counter || ""}&negative_stock=${negativeStock}&expired_stock=${expiredStock}&days=${daysParam}&search=${search}&for_csv=true`
      );

      if (res.data && res.data.success) {
        setPrintData(res.data.data);
        setTimeout(() => {
          handlePrint();
        }, 200);
      } else {
        showErrorToast({ title: "Print Error", message: "Failed to load print data." });
      }
    } catch (err) {
      showErrorToast({ title: "Print Error", message: err.message || "An error occurred." });
    } finally {
      setIsPrinting(false);
    }
  };

  // ─── Columns Definition ─────────────────────────────────────────────────────
  const columns = useMemo(() => {
    const commonCols = [
      {
        header: "Product Code",
        accessorKey: "product_code",
        size: "auto",
      },
      {
        header: "Product Name",
        accessorKey: "product_name",
        size: "auto",
        Cell: ({ cell }) => <Text fw={500} size="sm">{cell.getValue()}</Text>,
      },
      {
        header: "Batch No.",
        accessorKey: "batch_no",
        size: "auto",
        Cell: ({ cell }) => <Badge color="indigo" variant="light">{cell.getValue() || "-"}</Badge>,
      },
      {
        header: "Expiry Date",
        accessorKey: "exp_date",
        size: "auto",
        Cell: ({ cell }) => {
          const val = cell.getValue();
          return val ? moment(val).format("DD-MM-YYYY") : "-";
        },
      },
      {
        header: "Stock Qty",
        accessorKey: "stock",
        size: "auto",
        Cell: ({ cell }) => {
          const val = Number(cell.getValue()) || 0;
          let color = "gray";
          if (val < 0) {
            color = "grape";
          } else if (val === 0) {
            color = "dark";
          } else if (val > 0 && val < 10) {
            color = "red";
          } else if (val >= 10 && val < 50) {
            color = "yellow";
          } else if (val >= 50) {
            color = "green";
          }

          return (
            <Badge
              fz="12px"
              fw="bold"
              variant="light"
              color={color}
              radius="sm"
              px={8}
              py={2}
            >
              {Number(val).toFixed(3)}
            </Badge>
          );
        },
      },
    ];

    if (activeTab === "expiry_based") {
      // Insert "Days to Expire" after Stock Qty (so it is before Actions)
      const stockIdx = commonCols.findIndex((col) => col.accessorKey === "stock");
      const daysToExpireCol = {
        header: "Days to Expire",
        accessorKey: "days_to_expire",
        size: "auto",
        Cell: ({ cell }) => {
          const val = cell.getValue();
          if (val === null || val === undefined) return "-";
          const daysNum = Number(val);
          if (daysNum <= 0) {
            return (
              <Badge color="red" variant="filled" size="lg">
                {Math.abs(daysNum)} days ago
              </Badge>
            );
          } else if (daysNum <= 30) {
            return (
              <Badge color="orange" variant="light" size="lg">
                 In {daysNum} days
              </Badge>
            );
          } else {
            return (
              <Badge color="green" variant="light" size="lg">
                {daysNum} days left
              </Badge>
            );
          }
        },
      };
      commonCols.splice(stockIdx + 1, 0, daysToExpireCol);
    }

    // Add Edit Row Action
    commonCols.push({
      header: "Actions",
      accessorKey: "actions",
      size: 80,
      Cell: ({ row }) => (
        <ActionIcon
          color="brand"
          variant="light"
          onClick={() => {
            setEditingRow(row.original);
            setNewStock(row.original.stock);
            setNewExpDate(row.original.exp_date || "");
            setEditModalOpened(true);
          }}
        >
          <IconEdit size={16} />
        </ActionIcon>
      ),
    });

    return commonCols;
  }, [activeTab]);

  // ─── Search Handlers ────────────────────────────────────────────────────────
  const handleSearch = () => {
    setSearch(searchInput);
  };

  // ─── CSV Download Handler ───────────────────────────────────────────────────
  const downloadCsv = async () => {
    setIsCsvDownloading(true);
    try {
      const daysParam = expiredStock ? "" : days !== null ? days : "";
      const res = await httpClient.get(
        `stock_report/?type=${activeTab}&counter_id=${counter || ""}&negative_stock=${negativeStock}&expired_stock=${expiredStock}&days=${daysParam}&search=${search}&for_csv=true`
      );

      if (res.data && res.data.success) {
        const rows = res.data.data;
        const headers = [
          "Product Code",
          "Product Name",
          "Batch No.",
          "Expiry Date",
          ...(activeTab === "expiry_based" ? ["Days to Expire"] : []),
          "Stock Qty",
          "MRP",
          "Selling Price",
          "Total Value",
          "Counter Name",
        ];

        const csvRows = [headers];
        rows.forEach((r) => {
          csvRows.push([
            r.product_code,
            r.product_name,
            r.batch_no,
            r.exp_date ? moment(r.exp_date).format("DD-MM-YYYY") : "-",
            ...(activeTab === "expiry_based" ? [r.days_to_expire ?? "-"] : []),
            r.stock,
            r.mrp,
            r.selling_price,
            r.total,
            r.counter_name || "Global",
          ]);
        });

        setCsvData(csvRows);
        setTimeout(() => {
          if (csvRef.current && csvRef.current.link) {
            csvRef.current.link.click();
          }
        }, 100);
      } else {
        showErrorToast({ title: "Export Error", message: "Failed to load CSV export data." });
      }
    } catch (err) {
      showErrorToast({ title: "Export Error", message: err.message || "An error occurred." });
    } finally {
      setIsCsvDownloading(false);
    }
  };

  // ─── Print Component ────────────────────────────────────────────────────────
  const PrintComponent = () => (
    <div ref={printRef} style={{ padding: 25 }}>
      <h2 style={{ textAlign: "center", marginBottom: 5 }}>Stock Error Report Summary</h2>
      <p style={{ textAlign: "center", margin: 0, fontSize: 13, color: "#555" }}>
        Mode: {activeTab === "counter_based" ? "Negative Qty Stock" : "Expiry Date Based Stock"} | Counter: {countersList?.find(c => c.value === counter)?.label || "Warehouse"}
      </p>
      <table border="1" style={{ width: "100%", borderCollapse: "collapse", marginTop: 25, fontSize: 12 }} cellPadding="6">
        <thead>
          <tr style={{ background: "#f1f3f5" }}>
            <th>Product Code</th>
            <th>Product Name</th>
            <th>Batch No.</th>
            <th>Expiry Date</th>
            {activeTab === "expiry_based" && <th>Days to Expire</th>}
            <th>Stock Qty</th>
          </tr>
        </thead>
        <tbody>
          {printData?.map((r, i) => (
            <tr key={i}>
              <td>{r.product_code}</td>
              <td>{r.product_name}</td>
              <td style={{ textAlign: "center" }}>{r.batch_no || "-"}</td>
              <td style={{ textAlign: "center" }}>{r.exp_date ? moment(r.exp_date).format("DD-MM-YYYY") : "-"}</td>
              {activeTab === "expiry_based" && (
                <td style={{ textAlign: "center" }}>
                  {r.days_to_expire !== null && r.days_to_expire !== undefined
                    ? r.days_to_expire <= 0
                      ? `${Math.abs(r.days_to_expire)} days ago`
                      : `In ${r.days_to_expire} days`
                    : "-"}
                </td>
              )}
              <td style={{ textAlign: "right", fontWeight: "bold" }}>{Number(r.stock).toFixed(3)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <CSVLink
        style={{ display: "none" }}
        data={csvData}
        filename={`STOCK_REPORT_${activeTab.toUpperCase()}_${moment().format("DD_MM_YYYY")}.csv`}
        ref={csvRef}
      />

      <div style={{ display: "none" }}>
        <PrintComponent />
      </div>

      <Container fluid>
        <Box p={25}>
          <Flex justify="space-between" align="end" wrap="wrap" gap={10}>
            <Box>
              <Text size="lg" fw={600} mb={10}>
                Stock Error Reports Summary
              </Text>
              <AppBreadcrumb
                crumbs={[
                  { text: "Reports" },
                  { text: "Stock Error Report" },
                ]}
              />
            </Box>

            {/* TAB SELECTOR BUTTONS */}
            <Group spacing={0} sx={{ border: "1px solid #e9ecef", borderRadius: 8, padding: 2, background: "#f8f9fa" }}>
              <Button
                variant={activeTab === "counter_based" ? "filled" : "subtle"}
                color={activeTab === "counter_based" ? "brand" : "gray"}
                onClick={() => setActiveTab("counter_based")}
                styles={(theme) => ({
                  root: {
                    borderRadius: 6,
                    height: 36,
                    fontSize: 13,
                    fontWeight: 600,
                  },
                })}
              >
                Negative Qty Stock
              </Button>
              <Button
                variant={activeTab === "expiry_based" ? "filled" : "subtle"}
                color={activeTab === "expiry_based" ? "brand" : "gray"}
                onClick={() => setActiveTab("expiry_based")}
                styles={(theme) => ({
                  root: {
                    borderRadius: 6,
                    height: 36,
                    fontSize: 13,
                    fontWeight: 600,
                  },
                })}
              >
                Expiry Date Based Stock
              </Button>
            </Group>

            <Flex align="flex-end" gap={10}>
              <Button
                onClick={downloadCsv}
                leftIcon={<IconDownload size={16} />}
                loading={isCsvDownloading}
                variant="light"
                color="brand"
              >
                CSV Export
              </Button>
              <Button
                onClick={handlePrintAll}
                leftIcon={<IconPrinter size={16} />}
                loading={isPrinting}
                variant="light"
                color="brand"
              >
                Print
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Container>

      {/* FILTER CONTROL CARD */}
      <Container fluid mb={20}>
        <Box
          px={25}
          py={20}
          sx={(theme) => ({
            background: "#ffffff",
            borderRadius: 8,
            border: "1px solid #e9ecef",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          })}
        >
          <Flex direction={isMobileScreen ? "column" : "row"} gap={15} align={isMobileScreen ? "stretch" : "flex-end"} wrap="wrap">
            {/* COUNTER SELECTOR */}
            <Select
              label={
                <Flex align="center" gap={4}>
                  <IconBuilding size={14} color="#868e96" />
                  <Text fw={500}>Select Counter</Text>
                </Flex>
              }
              placeholder="Warehouse"
              value={counter}
              data={countersList || []}
              onChange={setCounter}
              clearable
              disabled={isCounterAdmin()}
              sx={{ minWidth: 200, flexGrow: 1 }}
            />

            {/* SEARCH PRODUCT / BATCH */}
            <TextInput
              label="Search Product / Batch"
              placeholder="Search product code, name, batch..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.currentTarget.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              sx={{ minWidth: 250, flexGrow: 2 }}
              rightSection={
                <Button
                  onClick={handleSearch}
                  size="xs"
                  variant="subtle"
                  color="brand"
                  styles={{ root: { padding: 4 } }}
                >
                  <IconSearch size={16} />
                </Button>
              }
            />

            {/* CONDITIONAL CONTROLS - COUNTER BASED MODE */}
            {activeTab === "counter_based" && (
              <Box sx={{ minWidth: 200, paddingBottom: 8 }}>
                <Switch
                  label="Show Only Negative Stock"
                  checked={negativeStock}
                  onChange={(e) => setNegativeStock(e.currentTarget.checked)}
                  color="red"
                  size="md"
                  thumbIcon={
                    negativeStock ? (
                      <Text size="9px" fw={800} color="red" align="center">-</Text>
                    ) : undefined
                  }
                />
              </Box>
            )}

            {/* CONDITIONAL CONTROLS - EXPIRY BASED MODE */}
            {activeTab === "expiry_based" && (
              <>
                <Select
                  label={
                    <Flex align="center" gap={4}>
                      <IconCalendar size={14} color="#868e96" />
                      <Text fw={500}>Expiry Within</Text>
                    </Flex>
                  }
                  placeholder="Select/Create expiry period"
                  value={selectedDaysOption}
                  data={expiryDaysOptions}
                  onChange={setSelectedDaysOption}
                  clearable
                  disabled={expiredStock}
                  sx={{ minWidth: 180, flexGrow: 1 }}
                />

                {selectedDaysOption === "custom" && !expiredStock && (
                  <NumberInput
                    label="Custom Days Amount"
                    value={customDays}
                    onChange={(val) => setCustomDays(val || 30)}
                    min={1}
                    max={9999}
                    sx={{ width: 120 }}
                  />
                )}

                <Box sx={{ minWidth: 180, paddingBottom: 8 }}>
                  <Switch
                    label="Show Expired Stock Only"
                    checked={expiredStock}
                    onChange={(e) => setExpiredStock(e.currentTarget.checked)}
                    color="red"
                    size="md"
                  />
                </Box>
              </>
            )}
          </Flex>
        </Box>
      </Container>

      {/* DATA TABLE */}
      <Container fluid mb={40}>
        <Box px={isMobileScreen ? 5 : 25}>
          <Box className="datatable-wrapper">
            <MantineReactTable
              key={activeTab}
              columns={columns}
              data={reportData?.data || []}
              isLoading={isLoading || isRefetching}
              state={{ isLoading: isLoading || isRefetching, pagination }}
              manualPagination
              rowCount={reportData?.count || 0}
              onPaginationChange={setPagination}
              mantineTableProps={{ className: "datatable", striped: true }}
              initialState={{ density: "xs" }}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableColumnActions={false}
              enableGlobalFilter={false}
              enableStickyHeader
            />
          </Box>
        </Box>
      </Container>

      {/* EDIT MODAL */}
      <Modal
        opened={editModalOpened}
        onClose={() => {
          setEditModalOpened(false);
          setEditingRow(null);
        }}
        title={
          <Text fw={600} size="md">
            Edit Stock & Expiry - {editingRow?.product_name}
          </Text>
        }
        centered
        size="md"
      >
        <Box p={10}>
          <Flex direction="column" gap={15}>
            <TextInput
              label="Product Code"
              value={editingRow?.product_code || ""}
              disabled
            />

            <TextInput
              label="Batch Number"
              value={editingRow?.batch_no || "-"}
              disabled
            />

            <NumberInput
              label="Stock Quantity"
              placeholder="Enter new stock"
              value={newStock}
              onChange={(val) => setNewStock(val || 0)}
              precision={3}
              step={0.001}
            />

            <TextInput
              label="Expiry Date"
              type="date"
              placeholder="Select Expiry Date"
              value={newExpDate}
              onChange={(e) => setNewExpDate(e.currentTarget.value)}
            />

            <Group position="right" mt={10}>
              <Button
                variant="subtle"
                color="gray"
                onClick={() => {
                  setEditModalOpened(false);
                  setEditingRow(null);
                }}
              >
                Cancel
              </Button>
              <Button
                color="brand"
                onClick={handleSaveEdit}
                loading={isSaving}
              >
                Save
              </Button>
            </Group>
          </Flex>
        </Box>
      </Modal>
    </>
  );
}
